import { useAlergiasCardQuery } from 'graphql/hooks.generated'
import { GrauCertezaAlergia, TipoAtendimentoProfissional } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useEffect, useMemo, useState } from 'react'
import { Route, useHistory, useRouteMatch } from 'react-router'
import { MetaArray } from 'util/metaPath'
import { ListaAlergiasModal } from 'view/atendimentos/detail/components/modals/alergias/ListaAlergiasModal'

import { AlergiaReacaoModel } from '../../avaliacao/components/alergias-reacoes/model'
import { ProntuarioCard } from '../ProntuarioCard'
import { concatenarOrigemTagAnalytics } from '../util/analyticsUtils'
import { AlergiasCard } from './AlergiasCard'
import { formatAlergias } from './format-alergias'

export interface AlergiasSectionProps {
  fieldName?: MetaArray<AlergiaReacaoModel>
  prontuarioId: ID
  alergiasAtendimentoAtual?: ReadonlyArray<AlergiaReacaoModel>
  expanded?: boolean
}

const GRAUS_CERTEZA = [GrauCertezaAlergia.CONFIRMADO, GrauCertezaAlergia.SUSPEITO, GrauCertezaAlergia.NAO_INFORMADO]

export function AlergiasSection({
  prontuarioId,
  alergiasAtendimentoAtual,
  fieldName: name,
  expanded = true,
}: AlergiasSectionProps) {
  const history = useHistory()
  const match = useRouteMatch()
  const [canRender, setCanRender] = useState(false)
  const modalUrl = `${match.path}/lista-alergias`
  const { analytics } = useAnalytics()

  const handleClick = () => {
    history.push(modalUrl)
    const tag = concatenarOrigemTagAnalytics('acessar_card_alergias', match.url)
    analytics.logEvent(tag)
  }

  const { data, loading } = useAlergiasCardQuery({
    variables: {
      filtro: {
        prontuarioId,
        grausCerteza: GRAUS_CERTEZA,
      },
      prontuarioId,
    },
  })

  const alergiasBd = data?.alergias?.content
  const { alergias, alergiasExpandido, alergiasAccordion } = useMemo(
    () => formatAlergias(alergiasBd, alergiasAtendimentoAtual),
    [alergiasAtendimentoAtual, alergiasBd]
  )

  useEffect(() => {
    setCanRender(alergiasExpandido.length !== 0)
  }, [alergiasExpandido])

  const atendimento = useAtendimentoContext()
  const { atendimentoProfissional, tiposAtendimento } = atendimento ?? {}
  const canEdit = atendimento && !tiposAtendimento.isAtendimentoProcedimentos && name
  const tooltip = alergias?.length
    ? `${canRender ? 'ver' : ''}${canRender && canEdit ? ' ou ' : ''}${
        canEdit ? 'adicionar' : ''
      } alergias/reações adversas`.capitalize()
    : 'Nenhuma alergia/reação adversa registrada'

  return (
    <>
      <Route
        path={modalUrl}
        render={(renderProps) =>
          canEdit ? (
            <ListaAlergiasModal
              {...renderProps}
              editable
              fieldName={name}
              prontuarioId={prontuarioId}
              alergiasAtendimentoAtual={alergiasAtendimentoAtual}
              dataAtendimento={Number(atendimentoProfissional.iniciadoEm)}
              isAtendimentoProcedimentos={
                atendimentoProfissional.tipo === TipoAtendimentoProfissional.ATENDIMENTO_PROCEDIMENTOS
              }
            />
          ) : (
            <ListaAlergiasModal
              {...renderProps}
              prontuarioId={prontuarioId}
              alergiasAtendimentoAtual={alergiasAtendimentoAtual}
            />
          )
        }
      />
      <ProntuarioCard
        title='Alergias/Reações adversas'
        tooltip={tooltip}
        onClick={handleClick}
        dataTestId='ProntuarioCard.alergia'
      >
        <AlergiasCard
          alergiasExpandido={alergiasExpandido}
          alergiasAccordion={alergiasAccordion}
          loading={loading}
          canRender={canRender}
          expanded={expanded}
        />
      </ProntuarioCard>
    </>
  )
}
